import {
  GroupConfigurator,
  HeaderModal,
  Icons,
} from "@kalyzee/escaet-ui";
import {useMemo, useState} from "react";
import {MeetingRoomBatchRoomCreate, MeetingRoomStatus} from "@kalyzee/kast-web-sdk/websocket";
import socketUserMeetingRoomsActions from "../../../store/meeting-room/actions";
import {useSocketAppDispatch} from "../../../hooks/app";
import {usePageContextRef} from "../../navigation/PageContext";
import {SessionMode} from "../../../store/session/slices";
import { GroupDistributionMode, GroupSizeConfiguration } from "@kalyzee/escaet-ui";

import styles from "./GroupConfiguratorExpertContainer.module.css";
import { useEscaetGroupCandidates, useEscaetGroupData } from "../../../hooks/meetingRoom";
import { arrayShuffleInPlace } from "../../../helpers/utils";
import { PopulatedMeetingRooms, PopulatedMeetingUser } from "../../../interfaces/populatedData";

export interface GroupExpertContainerProps {
  users: PopulatedMeetingUser[];
  rooms: PopulatedMeetingRooms;
  selfUserId: string;
  style?: React.CSSProperties;
  className?: string;
}

export const GroupConfiguratorExpertContainer = ({
  users,
  rooms,
  selfUserId,
  style,
  className
}: GroupExpertContainerProps) => {
  const dispatch = useSocketAppDispatch();
  
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();

  const [step, setStep] = useState(0);
  const [distributionMode, setDistributionMode] = useState<GroupDistributionMode>("random");
  const [groupSizeConfig, setGroupSizeConfig] = useState<GroupSizeConfiguration>();

  const preparingGroups = useMemo(
    () => rooms.groups.filter(group => group.room.status === MeetingRoomStatus.Enlistment), 
    [rooms],
  );

  const closedGroups = useMemo(
    () => rooms.groups.filter(group => group.room.status === MeetingRoomStatus.Closed), 
    [rooms],
  );

  const batchId = rooms.groups.length ? rooms.groups[0].room.batchId : undefined;
  
  const escaetCandidates = useEscaetGroupCandidates(users, selfUserId, true);
  
  const { 
    users: escaetUsers,
    groups: escaetGroups,
  } = useEscaetGroupData(preparingGroups, users, selfUserId);
  
  const onNextStep = () => setStep(s => s + 1);
  const onPreviousStep = () => setStep(s => s - 1);

  const generateGroupSizes = (groupNumber: number): number[] => {
    const usersPerGroup = Math.floor(escaetCandidates.length / groupNumber);

    const sizes: number[] = [];
    for (let i = 0; i < groupNumber; i++) {
      sizes.push(usersPerGroup);
    }

    const remaining = escaetCandidates.length % groupNumber;
    for (let i = 0; i < remaining; i++) {
      sizes[i]++;
    }

    return sizes;
  }

  const generateRandomGroups = (groupNumber: number): string[][] => {
    const sizes = generateGroupSizes(groupNumber);

    const users = [...escaetCandidates];
    arrayShuffleInPlace(users);

    const groups: string[][] = [];
    let i = 0;
    sizes.forEach((size) => {
      const group: string[] = [];
      for (let j = 0; j < size; j++) {
        group.push(users[i++].id);
      }
      groups.push(group);
    });

    return groups;
  }

  const generateRoomNameFromIndex = (index: number) => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const letterIndex = index % 26;
    const repeatCount = Math.floor(index / 26);
    const suffix = repeatCount > 0 ? (repeatCount + 1).toString() : "";
    return `${letters[letterIndex]}${suffix}`;
  }

  const createNewBatch = async () => {
    if (!groupSizeConfig) return; // impossible

    const id = pageContextRef.current?.params?.id;
    if (!id) return;
 
    let rooms: MeetingRoomBatchRoomCreate[] = [];
    if (distributionMode === "random") {
      rooms = generateRandomGroups(groupSizeConfig.number).map((group, idx) => ({
        enlistedUserIds: group,
        name: generateRoomNameFromIndex(idx),
        leaderUserId: group[Math.floor(Math.random() * group.length)],
        capacity: group.length,
      }));
    } else {
      rooms = generateGroupSizes(groupSizeConfig.number).map((size, idx) => ({
        name: generateRoomNameFromIndex(idx),
        capacity: size,
      }));
    }

    return await dispatch(socketUserMeetingRoomsActions.createBatch({
      meetingId: id,
      status: MeetingRoomStatus.Enlistment,
      rooms,
      audienceUserIds: escaetCandidates.map(user => user.id),
      mutuallyExclusive: true,
      deleteCurrentBatches: true,
      // no auto completion if we define the group ourself
      autoCompletionDelay: distributionMode === "manual" ? 30000 : undefined,
      chatrooms: true,
    }));
  }

  const completeExistingBatch = async () => {
    if (!batchId) return;

    await dispatch(socketUserMeetingRoomsActions.completeBatch({ 
      batchId: batchId,
    }));
  }

  const setBatchStatus = async (status: MeetingRoomStatus) => {
    if(batchId){
      // Set batch rooms status to ongoing
      await dispatch(socketUserMeetingRoomsActions.updateBatch({
        batchId: batchId,
        status,
        cancelAutoCompletion: true,
      }));
    }
  }

  const onValidateGroupConf = async (config: GroupSizeConfiguration) => {
    onNextStep();
    setGroupSizeConfig(config)
  };

  const renderConfigurator = () => {
    if (preparingGroups.length) {
      return (
        <GroupConfigurator.Overview
          onSetBatchRoomsToOngoing={() => setBatchStatus(MeetingRoomStatus.Ongoing)}
          onCompleteExistingBatchRooms={completeExistingBatch}
          groups={escaetGroups}
          groupUsers={escaetUsers}
          targetDate={preparingGroups[0].room.batchCompletionDate}
        />
      );
    }
    if (step === 0) {
      return (
        <GroupConfigurator.Init
          onCreateNewGroup={onNextStep}
          onReconstitutePreviousGroups={() => setBatchStatus(MeetingRoomStatus.Ongoing)}
          hasExistingGroups={!!closedGroups.length}
        />
      );
    } else if (step === 1) {
      return (
        <GroupConfigurator.Size
          groupUsers={escaetCandidates}
          onValidate={onValidateGroupConf}
          onCancel={() => setStep(0)} // TODO
        />
      );
    } else {
      return (
        <GroupConfigurator.Create
          onPreviousStep={onPreviousStep}
          onNextStep={createNewBatch} // rename to constitution or smth
          groupUsers={escaetCandidates}
          distributionMode={distributionMode}
          onDistributionModeChange={setDistributionMode}
        />
      );
    }
  }

  return (
    <div className={`${styles.containerGroupPage}${className ? ` ${className}` : ''}`} style={style}>
      <div className={styles.containerGroupModal}>
        <div style={{ flex: 2, marginLeft: 44, marginTop: 44 }}>
          <HeaderModal icon={Icons.Hand} title={"Créer un groupe"}/>
        </div>
        <div style={{ flex: 3, minWidth: 0 }}>
          {renderConfigurator()}
        </div>
      </div>
    </div>
  );
};

export default GroupConfiguratorExpertContainer;
